import { useCallback, useEffect, useRef, useState } from 'react';
// Imported from its own module, not the `@/utils` barrel: that barrel reaches
// axios and uuid, which drags an ESM dependency into every consumer's test.
import { extractApiErrorMessage } from '@/utils/errorHandler';

/**
 * One fetch, in one of three states. There is no fourth state and no implicit
 * fallback value: a card that cannot tell "zero" from "I do not know" will
 * eventually tell ops there is nothing to do on a day the backend was down.
 */
export type DashboardResource<T> =
  | { status: 'loading'; data: null; error: null }
  | { status: 'loaded'; data: T; error: null }
  | { status: 'failed'; data: null; error: string };

export interface DashboardResourceResult<T> {
  resource: DashboardResource<T>;
  /** Re-runs the fetch. Wire this to FeedbackError's retry. */
  reload: () => void;
}

const LOADING = { status: 'loading', data: null, error: null } as const;

/**
 * Fetches once per dependency change, guarded by the effect's own AbortSignal.
 *
 * One guard, not two. React runs an effect's cleanup before the next effect
 * body, so a superseded run is always aborted before its replacement starts —
 * a separate request-id counter would flip at exactly the same moments and
 * could never disagree with the signal.
 *
 * The guard is deliberately the signal and NOT the error's shape. `rentals.ts`
 * wraps every failure as `new Error(getBackendErrorMessage(error))`, a string,
 * so by the time a caller sees a rejection it can no longer tell a cancellation
 * from a real failure. Asking our own signal stays correct however the service
 * layer's error handling changes.
 *
 * Existing pages (`MaintenanceListPage`, `PurchaseOrderListPage`) hand-roll a
 * request-id without the abort half — their "AbortController" gates state and
 * cancels nothing, because no service function accepted a signal until now.
 * Migrating them is tracked in TODOS.md; this hook is proven on one consumer
 * first.
 */
export function useDashboardResource<T>(
  fetcher: (options: { signal: AbortSignal }) => Promise<T>,
  fallbackMessage = 'Gagal memuat data'
): DashboardResourceResult<T> {
  const [resource, setResource] = useState<DashboardResource<T>>(LOADING);
  const [attempt, setAttempt] = useState(0);

  // Read only on the failure path, so it must not re-trigger the fetch. Stable
  // for today's callers; a ref keeps it that way for the next one.
  const fallbackRef = useRef(fallbackMessage);
  fallbackRef.current = fallbackMessage;

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();

    setResource(LOADING);

    fetcher({ signal: controller.signal })
      .then((data) => {
        if (!controller.signal.aborted) {
          setResource({ status: 'loaded', data, error: null });
        }
      })
      .catch((error: unknown) => {
        // A superseded request is not a failure. Checking our own signal rather
        // than the error keeps that true even though the service layer has
        // already flattened the error to a string.
        if (controller.signal.aborted) return;
        setResource({
          status: 'failed',
          data: null,
          error: extractApiErrorMessage(error, fallbackRef.current),
        });
      });

    return () => controller.abort();
  }, [fetcher, attempt]);

  return { resource, reload };
}

export default useDashboardResource;
